"use client";

import {
  CosmosLink,
  CosmosText,
  CosmosTextProps,
  CosmosTitle,
  CosmosTitleProps,
} from "@cosmos/web-react";
import classNames from "classnames/bind";
import React from "react";
import styles from "./ProseCosmos.module.css";

const cx = classNames.bind(styles);

type Spacing = "long-form" | "long-form-bottom" | "long-form-top";

type OptionalCosmosTextProps = Pick<
  CosmosTextProps,
  "appearance" | "kind" | "size" | "spacing" | "tag" | "weight"
>;

type OptionalCosmosTitleProps = Pick<
  CosmosTitleProps,
  "appearance" | "kind" | "size" | "spacing" | "tag" | "weight"
>;

const COSMOS_TYPOGRAPHY_PROP_KEYS = [
  "appearance",
  "kind",
  "size",
  "spacing",
  "tag",
  "weight",
] as const;

function omitUndefinedProps<T extends object>(
  props: Partial<T>,
  keys: readonly (keyof T)[],
): Partial<T> {
  return Object.fromEntries(
    keys
      .filter((key) => props[key] !== undefined)
      .map((key) => [key, props[key]!]),
  ) as Partial<T>;
}

/**
 * Omit undefined CosmosText props so @lit/react does not overwrite web-component
 * defaults (e.g. kind="normal") by assigning `element.prop = undefined`.
 */
function definedCosmosTextProps(
  props: Partial<OptionalCosmosTextProps>,
): Partial<OptionalCosmosTextProps> {
  return omitUndefinedProps(props, COSMOS_TYPOGRAPHY_PROP_KEYS);
}

/**
 * Omit undefined CosmosTitle props so @lit/react does not overwrite web-component
 * defaults by assigning `element.prop = undefined`.
 */
function definedCosmosTitleProps(
  props: Partial<OptionalCosmosTitleProps>,
): Partial<OptionalCosmosTitleProps> {
  return omitUndefinedProps(props, COSMOS_TYPOGRAPHY_PROP_KEYS);
}

/** Generic props shape used when the exact element type is unknown at compile time. */
type NodeProps = { children?: React.ReactNode } & Record<string, unknown>;

interface ProseCosmosProps {
  children: React.ReactNode;
  className?: string;
  appearance?: "light" | "dark";
  size?: CosmosTextProps["size"];
  kind?: CosmosTextProps["kind"];
}

const TITLE_SIZES: NonNullable<CosmosTitleProps["size"]>[] = [
  "xx-small",
  "x-small",
  "small",
  "medium",
  "large",
  "x-large",
  "xx-large",
];

/**
 * h3 title size matches the prose text size 1:1.
 * Defaults to index 3 ("medium") when no size is specified.
 */
const TEXT_SIZE_TO_H3_INDEX: Partial<Record<string, number>> = {
  "xx-small": 0,
  "x-small": 1,
  small: 2,
  medium: 3,
  large: 4,
  "x-large": 5,
};

/**
 * Available `CosmosText` sizes ordered from smallest to largest.
 * Used to step a base size up or down by N levels (e.g. for `<figcaption>`).
 */
const TEXT_SIZES: NonNullable<CosmosTextProps["size"]>[] = [
  "xx-small",
  "x-small",
  "small",
  "medium",
  "large",
  "x-large",
];

/**
 * Return a text size shifted by `delta` levels from `baseSize`, clamped to the
 * available `TEXT_SIZES` range. Defaults to "medium" (index 3) when no
 * `baseSize` is provided.
 */
function shiftTextSize(
  baseSize: CosmosTextProps["size"] | undefined,
  delta: number,
): NonNullable<CosmosTextProps["size"]> {
  const startIndex = baseSize ? TEXT_SIZES.indexOf(baseSize) : 3;
  const index = Math.max(
    0,
    Math.min(TEXT_SIZES.length - 1, startIndex + delta),
  );
  return TEXT_SIZES[index];
}

/** Offset from h3's index for each heading level (h3 = 0 = base). */
const HEADING_LEVEL_DELTA: Record<number, number> = {
  1: 2,
  2: 1,
  3: 0,
  4: -1,
  5: -2,
  6: -2,
};

const HEADING_TAGS = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;
type HeadingTag = (typeof HEADING_TAGS)[number];

function isHeadingTag(tag: string): tag is HeadingTag {
  return (HEADING_TAGS as readonly string[]).includes(tag);
}

function getHeadingSize(
  level: number,
  baseSize?: CosmosTextProps["size"],
): NonNullable<CosmosTitleProps["size"]> {
  const h3Index = baseSize ? (TEXT_SIZE_TO_H3_INDEX[baseSize] ?? 3) : 3;
  const delta = HEADING_LEVEL_DELTA[level] ?? 0;
  const index = Math.max(0, Math.min(TITLE_SIZES.length - 1, h3Index + delta));
  return TITLE_SIZES[index];
}

/**
 * Compute Cosmos long-form spacing based on position among siblings.
 * First child  → "long-form-bottom" (no top margin)
 * Last child   → "long-form-top"    (no bottom margin)
 * Middle child → "long-form"        (both margins)
 * Only child   → undefined          (no spacing)
 */
function getSpacing(index: number, total: number): Spacing | undefined {
  if (total <= 1) return undefined;
  if (index === 0) return "long-form-bottom";
  if (index === total - 1) return "long-form-top";
  return "long-form";
}

function isSignificant(node: React.ReactNode): boolean {
  if (typeof node === "string") return node.trim().length > 0;
  return React.isValidElement(node);
}

/**
 * Tags that act as section breaks at the top level: they are rendered as bare
 * native elements (no CosmosText wrapper) and they reset long-form spacing on
 * their neighbors. The element itself relies on the prose CSS for its own
 * top/bottom margins.
 */
const SECTION_BREAK_TAGS = new Set([
  "hr",
  "img",
  "figure",
  "picture",
  "iframe",
]);

function isSectionBreak(node: React.ReactNode): boolean {
  return (
    React.isValidElement(node) &&
    typeof node.type === "string" &&
    SECTION_BREAK_TAGS.has(node.type)
  );
}

/**
 * For each top-level child, return its position within its section.
 * Section-break elements (see {@link SECTION_BREAK_TAGS}) end one section and
 * start a new one, so spacing restarts: the last block before a break gets
 * `long-form-top` (no bottom margin) and the first block after gets
 * `long-form-bottom` (no top margin). The break itself reports
 * `{ index: 0, total: 1 }` so it stays unspaced.
 */
function computeSectionPositions(
  nodes: React.ReactNode[],
): Array<{ index: number; total: number }> {
  const positions: Array<{ index: number; total: number }> = new Array(
    nodes.length,
  );
  let segmentStart = 0;

  const flushSegment = (endExclusive: number) => {
    const total = endExclusive - segmentStart;
    for (let i = segmentStart; i < endExclusive; i++) {
      positions[i] = { index: i - segmentStart, total };
    }
  };

  for (let i = 0; i < nodes.length; i++) {
    if (isSectionBreak(nodes[i])) {
      flushSegment(i);
      positions[i] = { index: 0, total: 1 };
      segmentStart = i + 1;
    }
  }
  flushSegment(nodes.length);

  return positions;
}

/**
 * Recursively unwrap React.Fragment nodes so their children are inlined as
 * siblings. This lets callers pass `<>…</>` or an array with nested fragments
 * and have every leaf node participate in sibling-count-based spacing.
 * All other element types (including `<div>`) are left untouched.
 */
function flattenFragments(nodes: React.ReactNode[]): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  for (const node of nodes) {
    if (
      React.isValidElement<{ children?: React.ReactNode }>(node) &&
      node.type === React.Fragment
    ) {
      const fragChildren = React.Children.toArray(node.props.children).filter(
        isSignificant,
      );
      result.push(...flattenFragments(fragChildren));
    } else {
      result.push(node);
    }
  }
  return result;
}

/**
 * Recursively walk a React tree and replace `<a>` elements with
 * `<CosmosLink>`, forwarding all props (href, target, rel, etc.).
 * Skips nodes that are already Cosmos components.
 */
function transformLinks(node: React.ReactNode): React.ReactNode {
  if (!React.isValidElement<NodeProps>(node)) return node;

  const { type, props } = node;

  if (type === CosmosLink || type === CosmosText || type === CosmosTitle) {
    return node;
  }

  if (type === "a") {
    const { children, ...rest } = props;
    return (
      <CosmosLink key={node.key} {...rest}>
        {React.Children.map(children, transformLinks)}
      </CosmosLink>
    );
  }

  if (props.children) {
    return React.cloneElement(node, {
      ...props,
      children: React.Children.map(props.children, transformLinks),
    });
  }

  return node;
}

/**
 * Block-level HTML tags that should be wrapped in `<CosmosText tag="div">`
 * to inherit Cosmos typography.  Their internals stay as plain HTML
 * (paragraphs and headings inside `<li>`, `<td>`, etc. are NOT converted).
 */
const BLOCK_WRAPPER_TAGS = new Set([
  "ul",
  "ol",
  "dl",
  "table",
  "blockquote",
  "pre",
  "details",
]);

/**
 * Block-level void tags that are self-contained (no children to wrap) and
 * should be wrapped in `<CosmosText tag="div">` for spacing. `<hr>`, `<img>`
 * and `<iframe>` are intentionally excluded — see {@link SECTION_BREAK_TAGS}.
 */
const BLOCK_VOID_TAGS = new Set(["video"]);

/**
 * Converts CMS HTML into Cosmos typography components.
 *
 * Direct children are transformed:
 *  - `<p>`          → `<CosmosText tag="p">`
 *  - `<h1>`–`<h6>` → `<CosmosTitle>`
 *  - Block elements (lists, tables, blockquotes, …) → wrapped in
 *    `<CosmosText tag="div">` so they inherit Cosmos typography and spacing.
 *    Their internals stay as plain HTML.
 *  - `<hr>`, `<img>`, `<figure>`, `<picture>`, `<iframe>` → rendered as
 *    native elements and treated as section breaks, so long-form spacing
 *    restarts on the next sibling and the prior sibling has its bottom margin
 *    removed.
 *  - `<a>` tags anywhere in the tree → `<CosmosLink>`
 */
export const ProseCosmos = ({
  children,
  className,
  appearance,
  size,
  kind,
}: ProseCosmosProps) => {
  const significantChildren = flattenFragments(
    React.Children.toArray(children).filter(isSignificant),
  );
  const positions = computeSectionPositions(significantChildren);

  /**
   * Transform a `<figure>` element: every direct `<figcaption>` child has its
   * contents wrapped in `<CosmosText tag="span">` so the caption picks up
   * Cosmos typography. All other figure children (e.g. `<img>`) only have
   * their links converted.
   */
  const transformFigure = (
    figure: React.ReactElement<NodeProps>,
  ): React.ReactNode => {
    const figureProps = figure.props;
    const newChildren = React.Children.map(figureProps.children, (figChild) => {
      if (
        React.isValidElement<NodeProps>(figChild) &&
        figChild.type === "figcaption"
      ) {
        const { children: captionChildren, ...captionRest } = figChild.props;
        return React.cloneElement(
          figChild,
          captionRest,
          <CosmosText
            {...definedCosmosTextProps({
              appearance,
              kind: "subtle",
              size: size !== undefined ? shiftTextSize(size, -1) : undefined,
              tag: "span",
            })}
          >
            {React.Children.map(captionChildren, transformLinks)}
          </CosmosText>,
        );
      }
      return transformLinks(figChild);
    });
    return React.cloneElement(figure, { ...figureProps }, newChildren);
  };

  const transformedChildren = significantChildren.map((child, index) => {
    const { index: sectionIndex, total: sectionTotal } = positions[index];
    const spacing = getSpacing(sectionIndex, sectionTotal);

    if (React.isValidElement<NodeProps>(child)) {
      const { type, props } = child;

      // Section-break elements (`<hr>`, `<img>`, `<figure>`, `<picture>`,
      // `<iframe>`) — render as-is (no CosmosText wrapper). Their margins come
      // from the prose CSS, and they reset long-form spacing on their
      // neighbors via `computeSectionPositions`. Links inside (e.g. inside a
      // `<figure>`) are still converted to `<CosmosLink>`. For `<figure>` we
      // additionally wrap any `<figcaption>` content in
      // `<CosmosText tag="span">`.
      if (typeof type === "string" && SECTION_BREAK_TAGS.has(type)) {
        if (type === "figure") {
          return transformFigure(child);
        }
        return transformLinks(child);
      }

      // Already a Cosmos component — add spacing if not explicitly set
      if (type === CosmosText || type === CosmosTitle) {
        if (!props.spacing && spacing !== undefined) {
          return React.cloneElement(
            child as React.ReactElement<{ spacing?: Spacing }>,
            { spacing },
          );
        }
        return child;
      }

      if (typeof type !== "string") return child;

      // <p> → <CosmosText>
      if (type === "p") {
        const { children: inner, ...rest } = props;
        return (
          <CosmosText
            key={child.key}
            {...rest}
            {...definedCosmosTextProps({
              appearance,
              kind,
              size,
              tag: "p",
              spacing,
            })}
          >
            {React.Children.map(inner, transformLinks)}
          </CosmosText>
        );
      }

      // <h1>–<h6> → <CosmosTitle>
      if (isHeadingTag(type)) {
        const level = parseInt(type.charAt(1), 10);
        const { children: inner, ...rest } = props;
        return (
          <CosmosTitle
            key={child.key}
            {...rest}
            {...definedCosmosTitleProps({
              appearance,
              tag: type,
              size: getHeadingSize(level, size),
              spacing,
            })}
          >
            {React.Children.map(inner, transformLinks)}
          </CosmosTitle>
        );
      }

      // Block elements → wrap in <CosmosText tag="div"> for typography + spacing
      if (BLOCK_WRAPPER_TAGS.has(type) || BLOCK_VOID_TAGS.has(type)) {
        return (
          <CosmosText
            key={child.key}
            {...definedCosmosTextProps({
              appearance,
              kind,
              size,
              tag: "div",
              spacing,
            })}
          >
            {transformLinks(child)}
          </CosmosText>
        );
      }

      // Any other element — transform links, no Cosmos wrapper
      return transformLinks(child);
    }

    // Bare text node at the top level
    if (typeof child === "string") {
      return (
        <CosmosText
          key={index}
          {...definedCosmosTextProps({
            appearance,
            kind,
            size,
            tag: "span",
            spacing,
          })}
        >
          {child}
        </CosmosText>
      );
    }

    return child;
  });

  return (
    <div className={cx("container", className)}>{transformedChildren}</div>
  );
};
